import { HEADER_H, LINE_H, PAGE_H, PAGE_W, ROWS, isHighlighter, type Paper, type Stroke, type TextBlock } from 'shared';
import { drawPaper, drawRowNumbers } from './paper.js';
import { paintStroke } from './render.js';

export interface PageImageInput {
  strokes: Stroke[];
  rowsToProcess: number[];
  paper: Paper;
  textBlocks?: TextBlock[];
}

/**
 * PNG da página para a leitura (SPEC §10.1.2). Gerado no tablet, não no
 * servidor: o desenho já está aqui, e mandar os traços para o servidor
 * redesenhar seria trabalho dobrado e uma segunda implementação do traçado
 * para divergir da primeira.
 */
export function renderPageImage({ strokes, rowsToProcess, paper, textBlocks = [] }: PageImageInput): Promise<Blob> {
  const rows = new Set(rowsToProcess);
  const live = strokes.filter((s) => !s.deletedAt);

  // Corta a altura no último traço + uma linha: menos pixels em branco, menos
  // tokens de imagem por leitura.
  let lastRow = 0;
  for (const s of live) lastRow = Math.max(lastRow, s.row);
  for (const r of rows) lastRow = Math.max(lastRow, r);
  for (const t of textBlocks) if (!t.deletedAt) lastRow = Math.max(lastRow, t.row);
  const height = Math.min(PAGE_H, Math.max(HEADER_H + 3 * LINE_H, HEADER_H + Math.min(ROWS, lastRow + 1) * LINE_H));

  const canvas = document.createElement('canvas');
  canvas.width = PAGE_W;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d indisponível');

  drawPaper(ctx, { paper: paper === 'pdf' ? 'blank' : paper, marks: new Map(), highlightRows: [] });

  for (const s of live) {
    if (isHighlighter(s.tool)) continue; // grifo não é conteúdo para a IA
    const isNew = rows.has(s.row) && !s.processedRunId;
    const isHeader = s.row === 0;
    // Preto = ler agora; cinza = contexto já lido. O cabeçalho é sempre preto
    // porque é o contexto da reunião.
    paintStroke(ctx, isNew || isHeader ? { ...s, color: '#111111' } : s, !isNew && !isHeader);
  }

  // Texto digitado vai para a imagem como texto impresso: é conteúdo da
  // página como o traço, e o modelo lê letra de forma muito melhor do que
  // letra à mão — o que digitar acerta quase sempre.
  ctx.save();
  ctx.font = '22px system-ui, sans-serif';
  ctx.textBaseline = 'top';
  for (const t of textBlocks) {
    if (t.deletedAt || !t.text.trim()) continue;
    const novo = rows.has(t.row) && !t.processedRunId;
    ctx.fillStyle = novo ? '#111111' : '#C9CED6';
    let y = t.y;
    for (const line of wrapText(ctx, t.text, t.width)) {
      ctx.fillText(line, t.x, y);
      y += 26;
    }
  }
  ctx.restore();

  drawRowNumbers(ctx, [...rows].sort((a, b) => a - b));

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('falha ao gerar o PNG'))), 'image/png');
  });
}

/** Miniatura da página para a biblioteca, gerada ao sair da página. */
export function renderThumbnail(strokes: Stroke[], paper: Paper, width = 220): string {
  const scale = width / PAGE_W;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = Math.round(PAGE_H * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.scale(scale, scale);
  drawPaper(ctx, { paper: paper === 'pdf' ? 'blank' : paper, marks: new Map(), highlightRows: [] });
  for (const s of strokes) if (!s.deletedAt) paintStroke(ctx, s);
  return canvas.toDataURL('image/webp', 0.6);
}

/** Quebra o texto na largura do bloco, para o PNG bater com o que se vê na tela. */
export function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const out: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const word of paragraph.split(' ')) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width > maxWidth && line) {
        out.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    out.push(line);
  }
  return out;
}
